import { ACTIONS } from "@wallyo/contracts/actions";
import type { AuditLogEntry, AuditOutcome } from "@wallyo/contracts/console";
import { useState, type ReactNode } from "react";
import { actionName, customerFeatureName, featureName, permissionName, settingName, t, type MessageId } from "../i18n";
import { ErrorNotice } from "../shell/ErrorNotice";
import {
  AUDIT_AREAS,
  DEFAULT_EFFECT,
  EFFECTS,
  OUTCOMES,
  auditHash,
  filterProblems,
  hasNarrowing,
  namesSomeone,
  withFilter,
  withoutNarrowing,
  type AuditFilters,
  type FilterName,
} from "./audit-filters";
import { describeActor, describeSides, formatWhen, settingOf, shortId, type Sides } from "./audit-format";
import { customerHref } from "./customers/customer-route";
import type { AuditLogState } from "./audit-log-state";

export type AuditLogViewProps = {
  filters: AuditFilters;
  state: AuditLogState;
  /** Only for the note under the table: which values are shown is the server's call, per entry. */
  canSeeSensitive: boolean;
  /** "Concerne" opens the customer's page for someone with VIEW_ACCOUNTS; otherwise it filters on them. */
  canViewAccounts?: boolean;
  onFilters: (next: AuditFilters) => void;
  onRetry: () => void;
  onLoadMore: () => void;
  now?: Date;
};

const areaLabel = (area: string) => t(`area.${area}` as MessageId);

/**
 * An entry's outcome as a pill: a refusal is red, an action that became an
 * approval request (ADR-0019 §5) is neither refused nor done, so it is neutral.
 */
const OUTCOME_TONE: Record<AuditOutcome, string> = {
  succeeded: "ok",
  denied: "bad",
  requested: "warn",
};

/** The end of a period that starts after it, said next to the box rather than by the server. */
function PeriodProblem({ problem }: { problem: MessageId | undefined }) {
  if (problem === undefined) return null;
  return (
    <span className="field-problem" id="audit-to-problem">
      {t(problem)}
    </span>
  );
}

function SearchProblem({ problem }: { problem: string | null }) {
  if (problem === null) return null;
  return <span className="subject-problem">{problem}</span>;
}

/**
 * What the server is asked: a person on one side of the change or the other,
 * and the period. Everything else narrows what came back, and lives in the
 * chips under it.
 */
function AuditSearchBar(props: {
  draft: AuditFilters;
  periodProblem: MessageId | undefined;
  problem: string | null;
  blocked: boolean;
  onSet: (name: FilterName, value: string) => void;
  onSearch: () => void;
}) {
  const { draft, periodProblem, problem, blocked, onSet, onSearch } = props;
  const uuidText = (value: number | undefined) => {
    if (value === undefined) return "";
    return String(value);
  };
  return (
    <form
      className="card subject-bar"
      aria-label={t("audit.searchAria")}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSearch();
      }}
    >
      {/* Two questions, two boxes: what did this person do, and what was done
          to this person. One box asking both answered neither. One of them is
          enough; neither is not a search. */}
      <label className="subject-uuid">
        <span className="subject-label">{t("audit.filter.updaterUuid")}</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={uuidText(draft.updaterUuid)}
          placeholder={t("features.subject.uuidHint")}
          onChange={(event) => onSet("updaterUuid", event.target.value)}
        />
      </label>
      <label className="subject-uuid">
        <span className="subject-label">{t("audit.filter.userUuid")}</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={uuidText(draft.userUuid)}
          placeholder={t("features.subject.uuidHint")}
          onChange={(event) => onSet("userUuid", event.target.value)}
        />
      </label>
      <label className="subject-date">
        <span className="subject-label">{t("audit.filter.from")}</span>
        <input
          type="date"
          value={draft.from ?? ""}
          max={draft.to}
          onChange={(event) => onSet("from", event.target.value)}
        />
      </label>
      <label className="subject-date">
        <span className="subject-label">{t("audit.filter.to")}</span>
        <input
          type="date"
          value={draft.to ?? ""}
          min={draft.from}
          aria-invalid={periodProblem === undefined ? undefined : true}
          aria-describedby={periodProblem === undefined ? undefined : "audit-to-problem"}
          onChange={(event) => onSet("to", event.target.value)}
        />
        <PeriodProblem problem={periodProblem} />
      </label>
      <button className="btn primary" type="submit" disabled={blocked}>
        {t("features.subject.search")}
      </button>
      <SearchProblem problem={problem} />
    </form>
  );
}

/**
 * The chips narrow the answer that already came back. They are server filters
 * today — each one asks again — so they write straight to the link rather
 * than waiting for Search, which asks about somebody else entirely.
 */
function AuditFilterChips({ filters, onFilters }: { filters: AuditFilters; onFilters: (next: AuditFilters) => void }) {
  const narrow = (name: FilterName) => (value: string) => onFilters(withFilter(filters, name, value));
  // A period whose end is before its start is refused by the server, so the
  // chip never writes one: the box holds what was typed and says what is
  // wrong, rather than spending a request to be told.
  const period = (name: "from" | "to") => (value: string) => {
    const next = withFilter(filters, name, value);
    if (filterProblems(next).to !== undefined) return;
    onFilters(next);
  };
  const periodProblem = filterProblems(filters).to;
  return (
    <div className="filter-chips" role="group" aria-label={t("audit.filtersAria")}>
      <label className="chip">
        <span>{t("audit.filter.action")}:</span>
        <select value={filters.actionId ?? ""} onChange={(event) => narrow("actionId")(event.target.value)}>
          <option value="">{t("audit.filter.allActions")}</option>
          {AUDIT_AREAS.map((area) => (
            <optgroup key={area} label={areaLabel(area)}>
              {ACTIONS.filter((action) => action.area === area).map((action) => (
                <option key={action.actionId} value={action.actionId}>
                  {actionName(action.actionId)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      <label className="chip">
        <span>{t("audit.filter.area")}:</span>
        <select value={filters.area ?? ""} onChange={(event) => narrow("area")(event.target.value)}>
          <option value="">{t("audit.filter.all")}</option>
          {AUDIT_AREAS.map((area) => (
            <option key={area} value={area}>
              {areaLabel(area)}
            </option>
          ))}
        </select>
      </label>
      <label className="chip">
        <span>{t("audit.filter.outcome")}:</span>
        <select value={filters.outcome ?? ""} onChange={(event) => narrow("outcome")(event.target.value)}>
          <option value="">{t("audit.filter.all")}</option>
          {OUTCOMES.map((outcome) => (
            <option key={outcome} value={outcome}>
              {t(`audit.outcome.${outcome}`)}
            </option>
          ))}
        </select>
      </label>
      {/* Changes or reads. The log keeps both; the screen shows changes,
          because a console that is being read writes far more reads than
          anybody makes changes and they push the changes off the page. */}
      <label className="chip">
        <span>{t("audit.filter.effect")}:</span>
        <select value={filters.effect ?? DEFAULT_EFFECT} onChange={(event) => narrow("effect")(event.target.value)}>
          {EFFECTS.map((effect) => (
            <option key={effect} value={effect}>
              {t(`audit.effect.${effect}`)}
            </option>
          ))}
        </select>
      </label>
      {/* The period narrows here as well as in the search bar. Neither end is
          required — you rarely know when the log starts — so a search with
          nobody's dates reads back from the newest entry, and somebody who
          knows what they are after can bound it before searching or after. */}
      <label className="chip">
        <span>{t("audit.filter.from")}:</span>
        <input
          type="date"
          value={filters.from ?? ""}
          max={filters.to}
          onChange={(e) => period("from")(e.target.value)}
        />
      </label>
      <label className="chip">
        <span>{t("audit.filter.to")}:</span>
        <input
          type="date"
          value={filters.to ?? ""}
          min={filters.from}
          aria-invalid={periodProblem === undefined ? undefined : true}
          onChange={(e) => period("to")(e.target.value)}
        />
      </label>
      <PeriodProblem problem={periodProblem} />
    </div>
  );
}

/** The chips, once there is something for them to narrow. */
function NarrowingChips(props: {
  state: AuditLogState;
  filters: AuditFilters;
  onFilters: (next: AuditFilters) => void;
}) {
  const { state, filters, onFilters } = props;
  if (state.status !== "ready") return null;
  if (state.entries.length === 0) return null;
  return <AuditFilterChips filters={filters} onFilters={onFilters} />;
}

/** The note under the table: reading the log is itself an entry in it. */
function AuditFoot({ canSeeSensitive }: { canSeeSensitive: boolean }) {
  const sensitive = t(canSeeSensitive ? "audit.sensitiveShown" : "audit.sensitiveHidden");
  return (
    <p className="foot">
      {t("audit.recorded")} {sensitive} {t("audit.deniedNote")}
    </p>
  );
}

function MoreError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <>
      <ErrorNotice error={error} />
      <p className="foot">{t("audit.moreFailed")}</p>
    </>
  );
}

function MoreButton({ state, onLoadMore }: { state: AuditLogState; onLoadMore: () => void }) {
  if (state.nextCursor === null) return null;
  let label = t("audit.loadMore");
  if (state.moreError) label = t("audit.retry");
  if (state.loadingMore) label = t("audit.loadingMore");
  return (
    <button className="btn" type="button" onClick={onLoadMore} disabled={state.loadingMore}>
      {label}
    </button>
  );
}

/** How much of the log is on screen, and whether that is all of it. */
function AuditMore({ state, onLoadMore }: { state: AuditLogState; onLoadMore: () => void }) {
  if (state.status !== "ready") return null;
  if (state.entries.length === 0) return null;
  let count = t("audit.count", { count: state.entries.length });
  if (state.entries.length === 1) count = t("audit.countOne");
  let end = "";
  if (state.nextCursor === null) end = ` · ${t("audit.end")}`;
  return (
    <div className="audit-more">
      <MoreError error={state.moreError} />
      <div className="btns">
        <MoreButton state={state} onLoadMore={onLoadMore} />
        <span className="meta" role="status">
          {count}
          {end}
        </span>
      </div>
    </div>
  );
}

/**
 * Everything under the search bar: nothing at all before the first search,
 * because a log of everybody is a dump rather than an answer.
 */
function AuditBody(props: {
  filters: AuditFilters;
  state: AuditLogState;
  blocked: boolean;
  canSeeSensitive: boolean;
  canViewAccounts: boolean;
  onFilters: (next: AuditFilters) => void;
  onRetry: () => void;
  onLoadMore: () => void;
  now?: Date;
}) {
  const { filters, state, blocked, canSeeSensitive, canViewAccounts, onFilters, onRetry, onLoadMore, now } = props;
  if (!namesSomeone(filters)) {
    return (
      <div className="card">
        <div className="empty">{t("features.subject.pickFirst")}</div>
      </div>
    );
  }
  if (blocked) {
    return (
      <div className="card">
        <div className="empty">{t("audit.fixProblems")}</div>
        <AuditFoot canSeeSensitive={canSeeSensitive} />
      </div>
    );
  }
  return (
    <>
      <NarrowingChips state={state} filters={filters} onFilters={onFilters} />
      <div className="card">
        <AuditResults
          state={state}
          filters={filters}
          canViewAccounts={canViewAccounts}
          onFilters={onFilters}
          onRetry={onRetry}
          now={now}
        />
        <AuditMore state={state} onLoadMore={onLoadMore} />
        <AuditFoot canSeeSensitive={canSeeSensitive} />
      </div>
    </>
  );
}

/**
 * Journal d'audit: name a person to search, then narrow what came back.
 *
 * The bar asks the server — who made the change, who it was about, and over
 * which period — and the chips under the results only narrow them. Nothing is
 * read until one of the two numbers is given, because the server refuses a
 * search that names nobody and the screen should say so first.
 */
export function AuditLogView({
  filters,
  state,
  canSeeSensitive,
  canViewAccounts = false,
  onFilters,
  onRetry,
  onLoadMore,
  now,
}: AuditLogViewProps) {
  const [draft, setDraft] = useState(filters);
  // A shared or cleared link wins over whatever was half-typed.
  const [seen, setSeen] = useState(auditHash(filters));
  const [problem, setProblem] = useState<string | null>(null);
  if (seen !== auditHash(filters)) {
    setSeen(auditHash(filters));
    setDraft(filters);
  }
  const problems = filterProblems(draft);
  const blocked = Object.keys(problems).length > 0;

  const set = (name: FilterName, value: string) => {
    setProblem(null);
    setDraft(withFilter(draft, name, value));
  };

  const search = () => {
    if (blocked) return;
    // The server refuses a search that names nobody (audit-log-validation.ts):
    // without a person the answer is the whole table. Said here, in words,
    // rather than spent on a request that comes back as an error.
    if (!namesSomeone(draft)) {
      setProblem(t("audit.problem.needsPerson"));
      return;
    }
    setProblem(null);
    onFilters(draft);
  };

  return (
    <>
      <AuditSearchBar
        draft={draft}
        periodProblem={problems.to}
        problem={problem}
        blocked={blocked}
        onSet={set}
        onSearch={search}
      />
      <AuditBody
        filters={filters}
        state={state}
        blocked={blocked}
        canSeeSensitive={canSeeSensitive}
        canViewAccounts={canViewAccounts}
        onFilters={onFilters}
        onRetry={onRetry}
        onLoadMore={onLoadMore}
        now={now}
      />
    </>
  );
}

/**
 * A search that found nothing. The chips are gone with the rows they narrowed,
 * so the way back to the person's whole log is here — dropping the chips, not
 * the search, which would send the screen back to asking who.
 */
function NoEntries({ filters, onFilters }: { filters: AuditFilters; onFilters: (next: AuditFilters) => void }) {
  if (!hasNarrowing(filters)) {
    return (
      <div className="empty" role="status">
        <span>{t("audit.emptyForPerson")}</span>
      </div>
    );
  }
  return (
    <div className="empty" role="status">
      <span>{t("audit.empty")}</span>
      <button className="btn" type="button" onClick={() => onFilters(withoutNarrowing(filters))}>
        {t("audit.filter.clear")}
      </button>
    </div>
  );
}

function AuditResults({
  state,
  filters,
  canViewAccounts,
  onFilters,
  onRetry,
  now,
}: Pick<AuditLogViewProps, "state" | "filters" | "onFilters" | "onRetry" | "now"> & { canViewAccounts: boolean }) {
  if (state.status === "loading") {
    return (
      <div className="empty" role="status" aria-live="polite">
        <span className="spinner" aria-hidden="true" />
        <span>{t("audit.loading")}</span>
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="audit-error">
        <p className="foot">{t("audit.loadFailed")}</p>
        <ErrorNotice error={state.error} />
        <div>
          <button className="btn primary" type="button" onClick={onRetry}>
            {t("audit.retry")}
          </button>
        </div>
      </div>
    );
  }
  if (state.entries.length === 0) {
    return <NoEntries filters={filters} onFilters={onFilters} />;
  }
  return (
    <div className="table-wrap">
      <table className="audit">
        <thead>
          <tr>
            <th>{t("audit.col.time")}</th>
            <th>{t("audit.col.subject")}</th>
            <th>{t("audit.col.actor")}</th>
            <th>{t("audit.col.action")}</th>
            <th>{t("audit.col.before")}</th>
            <th>{t("audit.col.after")}</th>
            <th>{t("audit.col.reason")}</th>
            <th>{t("audit.col.outcome")}</th>
          </tr>
        </thead>
        <tbody>
          {state.entries.map((entry) => (
            <AuditRow
              key={entry.auditLogId}
              entry={entry}
              filters={filters}
              canViewAccounts={canViewAccounts}
              onFilters={onFilters}
              now={now}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A scope in the reader's words. The server sends the stored value, and one
 * this build does not know yet shows as itself rather than as a blank: the
 * log is a record, so an unfamiliar scope must still be legible.
 */
const SCOPE_LABELS: Readonly<Record<string, MessageId>> = {
  user: "audit.scope.user",
  staffMember: "audit.scope.staffMember",
  staffRole: "audit.scope.staffRole",
  allStaff: "audit.scope.allStaff",
  allClients: "audit.scope.allClients",
  allPros: "audit.scope.allPros",
  proProfile: "audit.scope.proProfile",
  region: "audit.scope.region",
  appVersion: "audit.scope.appVersion",
  system: "audit.scope.system",
};

/**
 * What a setting is called, from whichever catalogue it belongs to.
 *
 * Four of them, and asking the wrong one falls back to the id — which is how
 * a permission id reached the screen. The suffix says which to ask.
 */
function settingLabel(settingId: string): string {
  if (settingId.endsWith("_STAFF_PERMISSION")) return permissionName(settingId);
  if (settingId.endsWith("_STAFF_FEATURE_ENABLEMENT")) return featureName(settingId as never);
  if (settingId.endsWith("_USER_FEATURE_ENABLEMENT")) return customerFeatureName(settingId as never);
  return settingName(settingId);
}

/**
 * One of the two value columns. A withheld entry says so on both sides rather
 * than once, so neither column can be read as an empty value.
 */
function SideCell({ side, kind, marker }: { side: readonly string[] | null; kind: Sides["kind"]; marker?: boolean }) {
  if (kind === "hidden") {
    return (
      <td>
        <span className="masked-value">
          <span className="masked">{t("audit.masked")}</span>
          {/* Why it is masked, said once for the pair rather than in both cells. */}
          {marker ? <span className="meta"> {t("audit.sensitive")}</span> : null}
        </span>
      </td>
    );
  }
  if (side === null) return <td>—</td>;
  return (
    <td>
      {side.map((line, index) => (
        <span key={index} className="change-line">
          {line}
        </span>
      ))}
    </td>
  );
}

function scopeLabel(scope: string | null): string {
  if (scope === null) return "—";
  const id = SCOPE_LABELS[scope];
  return id ? t(id) : scope;
}

function AuditRow({
  entry,
  filters,
  canViewAccounts,
  onFilters,
  now,
}: {
  entry: AuditLogEntry;
  filters: AuditFilters;
  canViewAccounts: boolean;
  onFilters: (next: AuditFilters) => void;
  now?: Date;
}) {
  const when = formatWhen(entry.occurredAt, now, { seconds: true });
  const actor = describeActor(entry);
  const change = describeSides(entry);
  const setting = settingOf(entry);
  const denied = entry.outcome === "denied";
  // A request is neither a success nor a refusal: it waits for someone.
  const outcomeTone = OUTCOME_TONE[entry.outcome];
  const filterOn = (name: FilterName, id: string, label: string) => (
    <button
      className="linkish"
      type="button"
      title={t("audit.filterBy", { id })}
      onClick={() => onFilters(withFilter(filters, name, id))}
    >
      {label}
    </button>
  );

  return (
    <tr className={denied ? "refused" : undefined} data-outcome={entry.outcome}>
      <td className="mono">
        {when.time}
        {when.day ? (
          <>
            <br />
            <span className="meta">{when.day}</span>
          </>
        ) : null}
      </td>
      <td>
        {/* Who it reached. A customer has a page to open; an employee is not
            an account, so they are their UUID and their name. A change written
            at a group scope reached whoever that scope reaches, which is the
            honest answer for a row that is about everybody. */}
        {entry.subjectUserId ? (
          <>
            {entry.subjectUuid === null ? null : <span className="mono uuid">{entry.subjectUuid} · </span>}
            <SubjectCell
              subjectUserId={entry.subjectUserId}
              label={entry.subjectLabel}
              canViewAccounts={canViewAccounts}
              filter={null}
            />
          </>
        ) : entry.subjectUuid !== null ? (
          <>
            <span className="mono uuid">{entry.subjectUuid}</span>
            {entry.subjectLabel ? (
              <>
                <br />
                <span className="meta">{entry.subjectLabel}</span>
              </>
            ) : null}
          </>
        ) : entry.scope !== null ? (
          scopeLabel(entry.scope)
        ) : (
          "—"
        )}
        {entry.onBehalfOfUserId ? (
          <>
            <br />
            <span className="meta">{t("audit.subject.onBehalf", { id: shortId(entry.onBehalfOfUserId) })}</span>
          </>
        ) : null}
      </td>
      <td>
        {entry.actorUuid === null ? null : <span className="mono uuid">{entry.actorUuid} · </span>}
        {entry.staffMemberId ? filterOn("staffMemberId", entry.staffMemberId, actor.name) : actor.name}
        <br />
        <span className="meta">{actor.detail}</span>
      </td>
      <td>{setting === null ? actionName(entry.actionId) : settingLabel(setting)}</td>
      {/* Before and after, a column each: the lines are in the same order on
          both sides, so line three on the left is line three on the right. */}
      <SideCell side={change.kind === "sides" ? change.before : null} kind={change.kind} />
      <SideCell side={change.kind === "sides" ? change.after : null} kind={change.kind} marker />
      <td>{entry.reason ? <bdi>{entry.reason}</bdi> : "—"}</td>
      <td>
        <span className={`pill ${outcomeTone}`}>{t(`audit.outcome.${entry.outcome}`)}</span>
      </td>
    </tr>
  );
}

/**
 * "Concerne": the subject's name as it is now, or their short id when they
 * have none (a deleted account). With VIEW_ACCOUNTS the name opens their page
 * and the short id under it still filters on them; without it, it filters.
 */
function SubjectCell({
  subjectUserId,
  label,
  canViewAccounts,
  filter,
}: {
  subjectUserId: string;
  label: string | null;
  canViewAccounts: boolean;
  filter: ReactNode;
}) {
  const name = label ?? shortId(subjectUserId);
  if (canViewAccounts) {
    return (
      <>
        <a className="subject-link" href={customerHref(subjectUserId)} title={t("audit.subject.open", { name })}>
          {name}
        </a>
        <br />
        <span className="mono meta">{filter}</span>
      </>
    );
  }
  if (!label) return <span className="mono">{filter}</span>;
  return (
    <>
      <span>{label}</span>
      <br />
      <span className="mono meta">{filter}</span>
    </>
  );
}
